import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ServerLeaderboardService } from '../../../services/ServerLeaderboardService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useLocation, useNavigate } from 'react-router-dom';
import FullPageSpinner from '../../FullPageSpinner';
import { computeScore } from '../../../utils/scoreUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type ServerStats = Awaited<ReturnType<typeof ServerLeaderboardService.getServerLeaderboardData>>[number];
type GroupedServerStats = ServerStats & { date: string };

const CombinedServerPerformanceAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  const searchParams = new URLSearchParams(location.search);

  // feat-133 Batch-3 (ADR 0003 DP-3a option-b): this view no longer runs an
  // unscoped all-tenants aggregation (which was a live cross-tenant leak for
  // Manager/Corporate). It scopes to a single tenant from auth context; an Admin
  // must select a tenant via TenantContext (bail like ServerScorecard/UpsellDashboard).
  const rawRole = user?.managementUser?.role;
  const normalizedRole = rawRole
    ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase()
    : null;
  const isAdminWithoutTenant = normalizedRole === 'Admin' && !selectedTenant;

  // Get parameters from URL
  const urlStartDate = searchParams.get('startDate');
  const urlEndDate = searchParams.get('endDate');

  const [startDate, setStartDate] = useState<string>(urlStartDate || '2025-03-01');
  const [endDate, setEndDate] = useState<string>(urlEndDate || format(new Date(), 'yyyy-MM-dd'));
  const [serverStats, setServerStats] = useState<GroupedServerStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Update URL when filters change
  useEffect(() => {
    if (startDate && endDate) {
      const params = new URLSearchParams();
      params.set('startDate', startDate);
      params.set('endDate', endDate);
      navigate(
        `/restaurant/servers/combined-performance?${params.toString()}`,
        { replace: true }
      );
    }
  }, [startDate, endDate, navigate]);

  useEffect(() => {
    fetchServerStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedTenant, isAdminWithoutTenant]);

  const fetchServerStats = async () => {
    // Admin must pick a tenant; without one there is no (cross-tenant) data to show.
    if (isAdminWithoutTenant) {
      setServerStats([]);
      return;
    }
    setIsLoading(true);
    try {
      // feat-133 Batch-3: scope the aggregation to the caller's tenant (auth
      // context) — non-admins get their own tenant (closing the prior leak),
      // admins the tenant they selected.
      const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;
      const rawStats = await ServerLeaderboardService.getServerLeaderboardData(
        startDate,
        endDate,
        undefined,
        true, // Group by date
        undefined, // No location filter
        true, // Skip transcript parts for better performance
        tenantId
      ) as GroupedServerStats[];

      console.log('Raw server stats from API:', JSON.stringify(rawStats, null, 2));
      console.log(`Total initial greetings for this server: ${rawStats.reduce((sum, stat) => sum + stat.initial_greeting_count, 0)}`);
      console.log(`Stats broken down by date:`, rawStats.map(stat => ({
        date: stat.date,
        server_name: stat.server_name,
        greetings: stat.initial_greeting_count,
        shifts: stat.shifts.map(shift => shift.shift_id)
      })));

      // For combined view, we need to aggregate all server stats by date
      const dateAggregates = new Map<string, GroupedServerStats>();

      rawStats.forEach(stat => {
        if (stat.initial_greeting_count === 0) return; // Skip entries with zero greetings

        const date = stat.date;
        if (!dateAggregates.has(date)) {
          // Create a new aggregate object for this date
          dateAggregates.set(date, {
            ...stat,
            server_id: 'combined',
            server_name: 'All Servers Combined',
            location_id: 'all',
            location_name: 'All Locations',
            shifts: [],
            date
          });
        } else {
          // Add stats from this server to the existing aggregate
          const aggregate = dateAggregates.get(date)!;
          aggregate.total_shifts += stat.total_shifts;
          aggregate.total_interactions += stat.total_interactions;
          aggregate.total_duration_minutes += stat.total_duration_minutes;
          aggregate.initial_greeting_count += stat.initial_greeting_count;
          aggregate.criteria_stats.upsell_count += stat.criteria_stats.upsell_count;
          aggregate.criteria_stats.mention_count += stat.criteria_stats.mention_count;
          aggregate.criteria_stats.describe_count += stat.criteria_stats.describe_count;
          aggregate.criteria_stats.drink_promotion_count += stat.criteria_stats.drink_promotion_count;
          aggregate.premium_stats.regular_sales += stat.premium_stats.regular_sales;
          aggregate.premium_stats.premium_sales += stat.premium_stats.premium_sales;
        }
      });

      // Recalculate premium conversion rate for each aggregate
      dateAggregates.forEach(aggregate => {
        if (aggregate.premium_stats.regular_sales > 0) {
          aggregate.premium_stats.premium_conversion_rate =
            (aggregate.premium_stats.premium_sales / aggregate.premium_stats.regular_sales) * 100;
        } else {
          aggregate.premium_stats.premium_conversion_rate = 0;
        }
      });

      // Convert to array and sort by date
      const aggregatedStats = Array.from(dateAggregates.values())
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      console.log('Combined server stats by date:', aggregatedStats);

      setServerStats(aggregatedStats);
    } catch (error) {
      console.error('Error fetching server stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate aggregate metrics
  const aggregateMetrics = serverStats.reduce((acc, stat) => {
    acc.totalGreetings += stat.initial_greeting_count;
    acc.totalUpsells += stat.criteria_stats.upsell_count;
    acc.totalMentions += stat.criteria_stats.mention_count;
    acc.totalDescriptions += stat.criteria_stats.describe_count;
    acc.totalDrinkPromotions += stat.criteria_stats.drink_promotion_count;
    acc.totalRegularSales += stat.premium_stats.regular_sales;
    acc.totalPremiumSales += stat.premium_stats.premium_sales;
    return acc;
  }, {
    totalGreetings: 0,
    totalUpsells: 0,
    totalMentions: 0,
    totalDescriptions: 0,
    totalDrinkPromotions: 0,
    totalRegularSales: 0,
    totalPremiumSales: 0,
  });

  // Calculate percentages
  const percentages = {
    upsell: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalUpsells / aggregateMetrics.totalGreetings) * 100 : 0,
    mention: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalMentions / aggregateMetrics.totalGreetings) * 100 : 0,
    description: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalDescriptions / aggregateMetrics.totalGreetings) * 100 : 0,
    drink: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalDrinkPromotions / aggregateMetrics.totalGreetings) * 100 : 0,
    premium: aggregateMetrics.totalRegularSales > 0 ? (aggregateMetrics.totalPremiumSales / aggregateMetrics.totalRegularSales) * 100 : 0,
  };

  // Calculate average score
  // HARDCODED FOR SAJJ; Multiply by 2 to get a score out of 100 since we expect each cashier and greeter to only upsell 2 out of the 4 items each.
  const averageScore = 2 * (percentages.upsell + percentages.mention + percentages.description + percentages.drink) / 4;

  // Add helper function for calculating rolling average
  const calculateRollingAverage = (data: number[], windowSize: number = 7): number[] => {
    const result: number[] = [];
    for (let i = 0; i < data.length; i++) {
      const start = Math.max(0, i - Math.floor(windowSize / 2));
      const end = Math.min(data.length, i + Math.ceil(windowSize / 2));
      const window = data.slice(start, end);
      const avg = window.reduce((sum, val) => sum + val, 0) / window.length;
      result.push(avg);
    }
    return result;
  };

  const miniChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.5,
        fill: false,
        stepped: false,
        borderWidth: 2,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 1,
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        position: 'top' as const,
        font: {
          size: 14,
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function (this: any, value: string | number) {
            return `${value}%`;
          },
        },
      },
      x: {
        display: true,
      },
    },
  };

  const getCriteriaChartData = (criteriaKey: keyof ServerStats['criteria_stats']) => {
    const rawData = serverStats.map(stat =>
      (stat.criteria_stats[criteriaKey] / stat.initial_greeting_count) * 100
    );
    const rollingAvg = calculateRollingAverage(rawData);

    return {
      labels: serverStats.map(stat => format(parseISO(stat.date), 'MMM d')),
      datasets: [{
        label: criteriaKey.replace('_count', '').replace('_', ' '),
        data: rawData,
        borderColor: {
          'upsell_count': 'rgb(255, 99, 132)',
          'mention_count': 'rgb(54, 162, 235)',
          'describe_count': 'rgb(255, 206, 86)',
          'drink_promotion_count': 'rgb(153, 102, 255)',
        }[criteriaKey],
        tension: 0.5,
        borderWidth: 2,
        pointRadius: 4,
      }, {
        label: '7-Day Average',
        data: rollingAvg,
        borderColor: {
          'upsell_count': 'rgba(255, 99, 132, 0.5)',
          'mention_count': 'rgba(54, 162, 235, 0.5)',
          'describe_count': 'rgba(255, 206, 86, 0.5)',
          'drink_promotion_count': 'rgba(153, 102, 255, 0.5)',
        }[criteriaKey],
        borderDash: [5, 5],
        tension: 0.5,
        borderWidth: 2,
        pointRadius: 0,
      }],
    };
  };

  const criteriaCharts = [
    { key: 'upsell_count' as const, title: 'Premium Menu %' },
    { key: 'mention_count' as const, title: 'Item Mentions %' },
    { key: 'describe_count' as const, title: 'Item Descriptions %' },
    { key: 'drink_promotion_count' as const, title: 'Drink Promotions %' },
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">
        Combined Server Performance Analysis
        <div className="text-sm font-normal text-gray-500 mt-1">
          {format(parseISO(startDate), 'MMM d, yyyy')} - {format(parseISO(endDate), 'MMM d, yyyy')}
        </div>
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <FullPageSpinner />
        </div>
      ) : serverStats.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '400px' }}>
              <Line
                data={{
                  labels: serverStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Greetings',
                    data: serverStats.map(stat => stat.initial_greeting_count),
                    borderColor: 'rgb(75, 192, 192)',
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 4,
                  }, {
                    label: '7-Day Average',
                    data: calculateRollingAverage(serverStats.map(stat => stat.initial_greeting_count)),
                    borderColor: 'rgba(75, 192, 192, 0.5)',
                    borderDash: [5, 5],
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 0,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      beginAtZero: true,
                      ticks: {
                        callback: function (this: any, value: string | number) {
                          return value;
                        },
                      },
                    },
                    x: {
                      display: false,
                    },
                  },
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Total Greetings',
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        font: {
                          size: 10
                        }
                      }
                    }
                  },
                }}
              />
            </div>

            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '400px' }}>
              <Line
                data={{
                  labels: serverStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Score',
                    data: serverStats.map(stat => {
                      if (stat.initial_greeting_count === 0) return 0;
                      const upsellPercent = Math.round((stat.criteria_stats.upsell_count / stat.initial_greeting_count) * 100);
                      const mentionPercent = Math.round((stat.criteria_stats.mention_count / stat.initial_greeting_count) * 100);
                      const describePercent = Math.round((stat.criteria_stats.describe_count / stat.initial_greeting_count) * 100);
                      const drinkPercent = Math.round((stat.criteria_stats.drink_promotion_count / stat.initial_greeting_count) * 100);
                      return computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);
                    }),
                    borderColor: 'rgb(255, 159, 64)',
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 4,
                  }, {
                    label: '7-Day Average',
                    data: calculateRollingAverage(serverStats.map(stat => {
                      if (stat.initial_greeting_count === 0) return 0;
                      const upsellPercent = Math.round((stat.criteria_stats.upsell_count / stat.initial_greeting_count) * 100);
                      const mentionPercent = Math.round((stat.criteria_stats.mention_count / stat.initial_greeting_count) * 100);
                      const describePercent = Math.round((stat.criteria_stats.describe_count / stat.initial_greeting_count) * 100);
                      const drinkPercent = Math.round((stat.criteria_stats.drink_promotion_count / stat.initial_greeting_count) * 100);
                      return computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);
                    })),
                    borderColor: 'rgba(255, 159, 64, 0.5)',
                    borderDash: [5, 5],
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 0,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Score (%)',
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        font: {
                          size: 10
                        }
                      }
                    }
                  },
                }}
              />
            </div>

            {criteriaCharts.map(({ key, title }) => (
              <div key={key} className="bg-white p-4 rounded-lg shadow" style={{ height: '400px' }}>
                <Line
                  data={getCriteriaChartData(key)}
                  options={{
                    ...miniChartOptions,
                    plugins: {
                      ...miniChartOptions.plugins,
                      title: {
                        ...miniChartOptions.plugins.title,
                        text: title,
                      },
                      legend: {
                        display: true,
                        position: 'top',
                        labels: {
                          usePointStyle: true,
                          boxWidth: 6,
                          font: {
                            size: 10
                          }
                        }
                      }
                    },
                  }}
                />
              </div>
            ))}

            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '400px' }}>
              <Line
                data={{
                  labels: serverStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Regular Sales',
                    data: serverStats.map(stat => stat.premium_stats.regular_sales),
                    borderColor: 'rgb(54, 162, 235)',
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 4,
                  }, {
                    label: 'Premium Sales',
                    data: serverStats.map(stat => stat.premium_stats.premium_sales),
                    borderColor: 'rgb(153, 102, 255)',
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 4,
                  }, {
                    label: '7-Day Average (Regular)',
                    data: calculateRollingAverage(serverStats.map(stat => stat.premium_stats.regular_sales)),
                    borderColor: 'rgba(54, 162, 235, 0.5)',
                    borderDash: [5, 5],
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 0,
                  }, {
                    label: '7-Day Average (Premium)',
                    data: calculateRollingAverage(serverStats.map(stat => stat.premium_stats.premium_sales)),
                    borderColor: 'rgba(153, 102, 255, 0.5)',
                    borderDash: [5, 5],
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 0,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      beginAtZero: true,
                      stacked: false,
                      ticks: {
                        callback: function (this: any, value: string | number) {
                          return value;
                        },
                      },
                    },
                    x: {
                      display: false,
                    },
                  },
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Total Sales',
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        font: {
                          size: 10
                        }
                      }
                    }
                  },
                }}
              />
            </div>

            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '400px' }}>
              <Line
                data={{
                  labels: serverStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Premium Conversion Rate',
                    data: serverStats.map(stat => stat.premium_stats.premium_conversion_rate),
                    borderColor: 'rgb(255, 159, 64)',
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 4,
                  }, {
                    label: '7-Day Average',
                    data: calculateRollingAverage(serverStats.map(stat => stat.premium_stats.premium_conversion_rate)),
                    borderColor: 'rgba(255, 159, 64, 0.5)',
                    borderDash: [5, 5],
                    tension: 0.5,
                    borderWidth: 2,
                    pointRadius: 0,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Premium Conversion Rate (%)',
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        font: {
                          size: 10
                        }
                      }
                    }
                  },
                }}
              />
            </div>
          </div>

          {/* Aggregate Performance Section */}
          <div className="mt-8 bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Aggregate Performance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Key Metrics</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Greetings</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalGreetings}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Average Score</span>
                      <span className="text-sm font-medium">{Math.round(averageScore)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(averageScore)}%`,
                          backgroundColor: 'rgb(255, 159, 64)'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Regular Sales</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalRegularSales}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Premium Sales</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalPremiumSales}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Sales</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalRegularSales + aggregateMetrics.totalPremiumSales}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Premium Conversion Rate</span>
                      <span className="text-sm font-medium">{Math.round(percentages.premium)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.premium)}%`,
                          backgroundColor: 'rgb(75, 192, 192)'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Criteria Performance</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Premium Menu</span>
                      <span className="text-sm font-medium">{Math.round(percentages.upsell)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.upsell)}%`,
                          backgroundColor: 'rgb(255, 99, 132)'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Item Mentions</span>
                      <span className="text-sm font-medium">{Math.round(percentages.mention)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.mention)}%`,
                          backgroundColor: 'rgb(54, 162, 235)'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Item Descriptions</span>
                      <span className="text-sm font-medium">{Math.round(percentages.description)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.description)}%`,
                          backgroundColor: 'rgb(255, 206, 86)'
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Drink Promotions</span>
                      <span className="text-sm font-medium">{Math.round(percentages.drink)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.drink)}%`,
                          backgroundColor: 'rgb(153, 102, 255)'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : isAdminWithoutTenant ? (
        <div className="text-center py-8 text-gray-500">Select a tenant to view combined performance.</div>
      ) : (
        <div className="text-center py-8 text-gray-500">No data available for the selected date range</div>
      )}

      {/* Debugging Section */}
      {serverStats.length > 0 && (
        <div className="mt-8 bg-gray-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Debugging Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded shadow">
              <div className="text-sm font-medium text-gray-500">Total Premium Mentions</div>
              <div className="text-xl font-bold">{aggregateMetrics.totalUpsells}</div>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <div className="text-sm font-medium text-gray-500">Total Item Mentions</div>
              <div className="text-xl font-bold">{aggregateMetrics.totalMentions}</div>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <div className="text-sm font-medium text-gray-500">Total Item Descriptions</div>
              <div className="text-xl font-bold">{aggregateMetrics.totalDescriptions}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CombinedServerPerformanceAnalysis; 